import React, { useEffect, useMemo, useState } from "react";
import Sidebar from "@/components/layout/Sidebar";
import TopBar from "@/components/layout/TopBar";
import { useSupabaseAuth } from "@/context/SupabaseAuthContext";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import {
  listNotifications,
  markNotificationRead,
  markNotificationReadWithStatus,
  deleteNotifications,
  NotificationRow,
} from "@/services/notificationService";
import { supabase } from "@/integrations/supabase/client";
import { GuestInvitationService } from "@/services/guestInvitationService";
import { Bell, CheckCircle, XCircle, Trash2 } from "lucide-react";

export default function Messages() {
  const navigate = useNavigate();
  const { user } = useSupabaseAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<NotificationRow[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [respondedMap, setRespondedMap] = useState<
    Record<string, "accepted" | "declined">
  >({});

  useEffect(() => {
    if (!user) {
      navigate("/");
      return;
    }
  }, [user, navigate]);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const rows = await listNotifications(user.id, 50);
      setItems(rows);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Realtime refresh for new notifications / read updates
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`notifications:${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          void load();
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const unreadCount = useMemo(
    () => items.filter((n) => !n.read_at).length,
    [items],
  );

  const acceptInvite = async (n: NotificationRow) => {
    const invitationId = n?.payload?.invitation_id;
    if (!invitationId) return;

    const { data: json, error } = await supabase.functions.invoke(
      "accept-guest-invitation",
      { body: { invitation_id: invitationId } },
    );

    if (error || !json?.success) {
      const code = (json as any)?.error || error?.message;
      toast({
        title: "Failed to accept invite",
        description:
          code === "insufficient_credits"
            ? "The inviter doesn't have enough credits to accept this invite right now."
            : (json as any)?.error || error?.message || "Please try again.",
        variant: "destructive",
      });
      return;
    }

    setRespondedMap((prev) => ({ ...prev, [n.id]: "accepted" }));
    await markNotificationReadWithStatus(n.id, "accepted", n.payload);
    toast({
      title: "Invitation accepted",
      description: "Redirecting to your projects…",
    });
    navigate("/projects");
  };

  const declineInvite = async (n: NotificationRow) => {
    const invitationId = n?.payload?.invitation_id;
    if (!invitationId) return;

    const res = await GuestInvitationService.declineInvitation(invitationId);
    if (!res.success) {
      toast({
        title: "Failed to decline invite",
        description: res.error || "Please try again.",
        variant: "destructive",
      });
      return;
    }

    setRespondedMap((prev) => ({ ...prev, [n.id]: "declined" }));
    await markNotificationReadWithStatus(n.id, "declined", n.payload);
    toast({ title: "Invitation declined" });
    void load();
  };

  const markRead = async (n: NotificationRow) => {
    if (n.read_at) return;
    await markNotificationRead(n.id);
    void load();
  };

  const handleDelete = async (ids: string[]) => {
    console.log("Attempting to delete notifications:", ids);
    if (!user?.id) {
      toast({
        title: "User not found",
        description: "You must be signed in to delete messages.",
        variant: "destructive",
      });
      return;
    }
    const ok = await deleteNotifications(ids, user.id);
    console.log("Delete result from backend:", ok);
    if (!ok) {
      toast({
        title: "Failed to delete",
        description:
          "Could not delete the selected messages. Please try again.",
        variant: "destructive",
      });
      return;
    }
    setItems((prev) => prev.filter((n) => !ids.includes(n.id)));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));
      return next;
    });
    toast({
      title:
        ids.length === 1 ? "Message deleted" : `${ids.length} messages deleted`,
    });
    // Always reload from backend after delete
    console.log("Reloading notifications from backend after delete...");
    void load();
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allSelected = items.length > 0 && selectedIds.size === items.length;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map((n) => n.id)));
    }
  };

  const getInviteStatus = (
    n: NotificationRow,
  ): "accepted" | "declined" | "pending" => {
    if (respondedMap[n.id]) return respondedMap[n.id];
    if (n.payload?.status === "accepted") return "accepted";
    if (n.payload?.status === "declined") return "declined";
    return "pending";
  };

  return (
    <div className="min-h-screen bg-background flex relative overflow-x-hidden">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-y-auto">
        <TopBar
          title="Messages"
          icon={<Bell className="h-4 w-4 text-[#40e0d0]" />}
        />

        <main className="flex-1 p-4 md:p-8 lg:p-10">
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl font-semibold">Messages</h1>
                <p className="text-sm text-muted-foreground">
                  Invitations and system notifications.
                </p>
              </div>
              <Badge variant="outline">{unreadCount} unread</Badge>
            </div>

            <Card className="border-border/50">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    {items.length > 0 && (
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={toggleSelectAll}
                        aria-label="Select all messages"
                      />
                    )}
                    <CardTitle className="text-base">Notifications</CardTitle>
                  </div>
                  {selectedIds.size > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">
                        {selectedIds.size} selected
                      </span>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(Array.from(selectedIds))}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete selected
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {loading ? (
                  <div className="text-sm text-muted-foreground">Loading…</div>
                ) : items.length === 0 ? (
                  <div className="text-sm text-muted-foreground">
                    No messages yet.
                  </div>
                ) : (
                  items.map((n) => {
                    const isInvite = n.type === "guest_invite";
                    const unread = !n.read_at;
                    const inviteStatus = isInvite
                      ? getInviteStatus(n)
                      : "pending";
                    const isSelected = selectedIds.has(n.id);
                    return (
                      <div
                        key={n.id}
                        className={`rounded-xl border p-4 flex flex-col gap-2 transition-colors ${
                          isSelected
                            ? "bg-[#40e0d0]/10 border-[#40e0d0]/40"
                            : unread
                              ? "bg-[#40e0d0]/5 border-[#40e0d0]/20"
                              : "bg-background"
                        }`}
                      >
                        <div className="flex items-start gap-3 text-left">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleSelect(n.id)}
                            aria-label={`Select message: ${n.title}`}
                            className="mt-0.5 shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-4">
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <div className="font-medium truncate">
                                    {n.title}
                                  </div>
                                  {unread && (
                                    <Badge className="bg-[#40e0d0] text-black shrink-0">
                                      New
                                    </Badge>
                                  )}
                                  {isInvite && inviteStatus === "accepted" && (
                                    <Badge
                                      variant="outline"
                                      className="text-green-600 border-green-600 shrink-0"
                                    >
                                      <CheckCircle className="h-3 w-3 mr-1" />
                                      Accepted
                                    </Badge>
                                  )}
                                  {isInvite && inviteStatus === "declined" && (
                                    <Badge
                                      variant="outline"
                                      className="text-muted-foreground shrink-0"
                                    >
                                      <XCircle className="h-3 w-3 mr-1" />
                                      Declined
                                    </Badge>
                                  )}
                                </div>
                                {n.body && (
                                  <div className="text-sm text-muted-foreground mt-1">
                                    {n.body}
                                  </div>
                                )}
                                <div className="text-xs text-muted-foreground mt-2">
                                  {new Date(n.created_at).toLocaleString()}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                {unread && inviteStatus === "pending" && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => markRead(n)}
                                  >
                                    Mark read
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-muted-foreground hover:text-destructive"
                                  onClick={() => handleDelete([n.id])}
                                  aria-label="Delete message"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>

                            {isInvite && inviteStatus === "pending" && (
                              <div className="flex gap-2 pt-2">
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => acceptInvite(n)}
                                >
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  Accept
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => declineInvite(n)}
                                >
                                  <XCircle className="h-4 w-4 mr-2" />
                                  Decline
                                </Button>
                              </div>
                            )}

                            {isInvite && inviteStatus !== "pending" && (
                              <div className="flex gap-2 pt-2">
                                <Button
                                  size="sm"
                                  disabled
                                  className="bg-green-600/40 text-green-800 cursor-not-allowed opacity-60"
                                >
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  Accept
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled
                                  className="opacity-60 cursor-not-allowed"
                                >
                                  <XCircle className="h-4 w-4 mr-2" />
                                  Decline
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </div>
  );
}
